/**
 * 月結對帳單(REQ-20260806002)—— 一個客戶、一個期間的應收明細,
 * 給業務列印 / 匯出後向客戶請款。
 *
 * 期間取法跟客戶主檔的 `settlementCycle` 綁在一起:月結 / 次月結才有對帳單,
 * 貨到付款與現金客戶按單結清,做月結對帳單沒有意義(仍可查,只是不預設)。
 *
 * REQ-20260831001 起本檔也放**廠商對帳單**(`SupplierStatement`,見檔尾)——
 * 期間 query 與明細列的形狀兩側共用,只有抬頭與合計欄位各自一份。
 */
import { z } from 'zod';

const ymd = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, '日期格式須為 YYYY-MM-DD');

/**
 * GET /statements/customer/:id 的 query(REQ-20260831001 起
 * GET /statements/supplier/:id 也走同一份 —— 期間規則兩側完全一樣)。
 *
 * 兩種期間指定方式,擇一:
 *  - `period=YYYY-MM` —— 自然月(既有行為,沒帶 from/to 時走這條)
 *  - `from` + `to` —— 任意起訖日期(REQ-20260815013),兩端都**含當天**。
 *    只給其中一邊沒有意義:對帳單的期初 / 期末各需要一個邊界,所以要求成對出現。
 */
export const StatementQuerySchema = z
  .object({
    /** 期間,YYYY-MM(台北時區的自然月) */
    period: z
      .string()
      .regex(/^\d{4}-\d{2}$/, '期間格式須為 YYYY-MM')
      .optional(),
    /** 自訂區間起日(含當天) */
    from: ymd.optional(),
    /** 自訂區間迄日(含當天) */
    to: ymd.optional(),
  })
  .refine((q) => q.period != null || (q.from != null && q.to != null), {
    message: '請指定 period(YYYY-MM)或成對的 from / to(YYYY-MM-DD)',
  });
export type StatementQuery = z.infer<typeof StatementQuerySchema>;

/**
 * GET /statements/customer/:id/personal-outstanding 的 query(REQ-20260810002)。
 *
 * 月結對帳單只結公帳(`payerType <> 'PERSONAL'`),私帳單改由這支查 ——
 * 會計要向某個業務 / 聯絡人本人收現時,列他個人的未付明細。
 * `payerName` 必填:沒有「全部私帳」這個用途,一次就是對一個人請款。
 */
export const PersonalOutstandingQuerySchema = z.object({
  payerName: z.string().trim().min(1, '請指定帳款對象').max(100),
});
export type PersonalOutstandingQuery = z.infer<typeof PersonalOutstandingQuerySchema>;

/**
 * 對帳單明細的品項列(REQ-20260815013)—— 一張出貨單底下的一個品項。
 *
 * 金額欄位一律是 DECIMAL 字串(與單頭同一個慣例);`qty` 是 DECIMAL(14,4),
 * 序列化時已由 Decimal 去掉尾隨的零(`5.0000` → `5`)。
 */
export interface StatementLineItem {
  lineId: number;
  productCode: string;
  productName: string;
  productSpec: string | null;
  unitName: string | null;
  qty: string;
  unitPrice: string;
  amount: string;
  notes: string | null;
}

/** 對帳單上的一列:一張出貨單(單頭金額 + 底下的品項列) */
export interface StatementLine {
  orderId: number;
  orderNo: string;
  txnDate: string;
  /**
   * 這張單的品項。**可能是空陣列** —— 歷史 mdb 匯入單只有單頭金額、
   * 對不到品項明細時就沒有品項列(`mdb-import.service.ts` 只在有品項時才建)。
   */
  items: StatementLineItem[];
  subtotal: string;
  tax: string;
  total: string;
  allowanceAmount: string | null;
  settledAmount: string;
  outstanding: string;
}

/**
 * 期間內生效的銷貨折讓單(REQ-20260821001)。
 *
 * 「生效」看的是 `effectivePeriod`(= 折讓單的 `appliedPeriod`,沒填就是開立日的次月),
 * 不是開立日期 —— 需求要的正是「這個月談定、下個月的帳單才扣」。
 */
export interface StatementCreditNote {
  creditNoteId: number;
  documentNo: string;
  orderNo: string;
  issuedDate: string;
  amount: string;
  reason: string;
}

/** 期間內的收款紀錄 */
export interface StatementPayment {
  settlementId: number;
  documentNo: string;
  settledAt: string;
  totalSettled: string;
}

export interface CustomerStatement {
  customerId: number;
  customerCode: string | null;
  customerFullName: string;
  customerSimpleName: string;
  taxId: string | null;
  address: string | null;
  phone: string | null;
  contact: string | null;
  settlementCycle: string | null;
  settlementCycleLabel: string | null;
  settlementDay: number | null;
  /** 付款憑證(發票 / 收據),來自客戶主檔的 paymentCert */
  paymentCert: string | null;

  /** 自訂區間查詢(from/to)時為 null —— 那時期間不對應任何一個自然月 */
  period: string | null;
  periodStart: string;
  periodEnd: string;
  /** 依結帳週期推算的請款到期日 */
  dueDate: string;

  /** 期初未結:期間開始前所有已過帳出貨單的未結餘額合計(含期初應收) */
  openingBalance: string;
  /**
   * 客戶主檔的期初預收貨款(REQ-20260815013)。
   *
   * ⚠️ 這是**一次性的期初設定值**,系統目前沒有逐期追蹤預收動用的機制,
   * 所以它**不參與**「期初 + 本期出貨 - 折讓 - 收款 = 期末」這條滾動式,
   * 只是照著客戶主檔顯示。前端要一併標示「僅期初設定值」。
   */
  openingPrepayment: string | null;
  /** 本期出貨未稅合計(= 各單 subtotal 之和,含版費 / 刀模費) */
  periodSubtotal: string;
  /** 本期出貨稅額合計 */
  periodTax: string;
  /** 本期出貨合計(含稅)= periodSubtotal + periodTax */
  periodSales: string;
  /**
   * 本期折讓合計 = 本期出貨單的單頭折讓(`allowanceAmount`)
   * **加上**本期生效的折讓單金額(REQ-20260821001)。
   * 兩者來自不同資料來源(單頭欄位 vs `sales_order_credit_notes`),不會重複計算。
   */
  periodAllowance: string;
  /** 本期收款合計 */
  periodPayments: string;
  /** 期末應收 = 期初 + 本期出貨 - 本期折讓 - 本期收款 */
  closingBalance: string;

  lines: StatementLine[];
  payments: StatementPayment[];
  /** 本期生效的折讓單(REQ-20260821001)。沒有就是空陣列 */
  creditNotes: StatementCreditNote[];
}

/**
 * 廠商對帳單(REQ-20260831001)—— 應付側的月結對帳單,與 `CustomerStatement` 同構。
 *
 * `StatementQuerySchema` / `StatementLine` / `StatementLineItem` / `StatementPayment`
 * **直接複用上面那幾份**:期間的指定方式(period 或成對 from/to)與明細的欄位形狀
 * 兩側完全一樣,複製第二份只會多一個會漂移的真相。
 *
 * 兩處與應收側刻意不同:
 *  - **沒有 `creditNotes`** —— 折讓單(`sales_order_credit_notes`)只有應收側有,
 *    進貨側的折讓只有單頭 `allowanceAmount` 一個來源。
 *  - **沒有私帳(`payerType`)概念** —— 進貨單沒有「向某個聯絡人本人收」這回事,
 *    所以沒有應收側那三段私帳扣減。
 */
export interface SupplierStatement {
  supplierId: number;
  supplierCode: string | null;
  supplierFullName: string;
  supplierSimpleName: string;
  taxId: string | null;
  address: string | null;
  phone: string | null;
  contact: string | null;
  /** 付款條件(廠商主檔的自由文字,例「月結」「即期」) */
  paymentTerms: string | null;
  /** 付款天數 */
  paymentDays: number | null;
  /** 結帳日(每月幾號) */
  closingDay: number | null;

  /** 自訂區間查詢(from/to)時為 null —— 那時期間不對應任何一個自然月 */
  period: string | null;
  periodStart: string;
  periodEnd: string;

  /** 期初未付:期初應付 + 期間前所有已過帳進貨單(扣折讓)- 期間前所有付款 */
  openingBalance: string;
  /**
   * 廠商主檔的期初預付貨款。
   *
   * ⚠️ 與應收側的 `openingPrepayment` 同一個性質:**一次性的期初設定值**,
   * 系統沒有逐期追蹤預付動用的機制,所以它**不參與**滾動式,只是照主檔顯示。
   * 前端要一併標示「僅期初設定值」(理由見 REQ-20260815013 §四)。
   */
  openingPrepaid: string | null;
  /** 本期進貨未稅合計(= 各單 subtotal 之和) */
  periodSubtotal: string;
  /** 本期進貨稅額合計 */
  periodTax: string;
  /** 本期進貨合計(含稅)= periodSubtotal + periodTax */
  periodPurchases: string;
  /** 本期折讓合計 —— 只有進貨單單頭的 `allowanceAmount`(進貨側沒有折讓單資料表) */
  periodAllowance: string;
  /** 本期付款合計 */
  periodPayments: string;
  /** 期末應付 = 期初 + 本期進貨 - 本期折讓 - 本期付款 */
  closingBalance: string;

  lines: StatementLine[];
  payments: StatementPayment[];
}
